import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { 
  UserCheck, 
  Calendar, 
  Video, 
  Star, 
  Award, 
  Clock,
  MessageSquare,
  CheckCircle,
  Users,
  Sparkles,
  UserPlus,
  Edit,
  Trash2,
  X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SistemaMentoria = ({ pdiId, colaboradorId, competenciaId }) => {
  const [loading, setLoading] = useState(false);
  const [mentores, setMentores] = useState([]);
  const [mentoresRecomendados, setMentoresRecomendados] = useState([]);
  const [sessoes, setSessoes] = useState([]);
  const [showAgendarModal, setShowAgendarModal] = useState(false);
  const [mentorSelecionado, setMentorSelecionado] = useState(null);
  
  // Estados para detalhes da sessão
  const [showDetalhesModal, setShowDetalhesModal] = useState(false);
  const [sessaoSelecionada, setSessaoSelecionada] = useState(null);
  
  // Estados para gestão de mentores
  const [abaAtiva, setAbaAtiva] = useState('sessoes'); // 'sessoes' ou 'mentores'
  const [showMentorModal, setShowMentorModal] = useState(false);
  const [mentorEditando, setMentorEditando] = useState(null);
  const [colaboradores, setColaboradores] = useState([]);
  
  const [novoMentor, setNovoMentor] = useState({
    colaborador_id: '',
    nome: '',
    cargo: '',
    area: '',
    competencias_mentoria: [],
    anos_experiencia: 0,
    disponivel: true,
    capacidade_mentorados: 3,
    horas_disponiveis_semana: 2,
    bio: ''
  });
  
  const [novaSessao, setNovaSessao] = useState({
    pdi_id: pdiId || `pdi_${colaboradorId}_mentoria`,
    mentorado_id: colaboradorId,
    mentor_id: '',
    data_agendada: '',
    duracao_minutos: 60,
    objetivo: '',
    topicos: []
  });

  // Atualizar pdi_id quando colaboradorId mudar
  useEffect(() => {
    setNovaSessao(prev => ({
      ...prev,
      pdi_id: pdiId || `pdi_${colaboradorId}_mentoria`,
      mentorado_id: colaboradorId
    }));
  }, [pdiId, colaboradorId]);

  useEffect(() => {
    const effectivePdiId = pdiId || `pdi_${colaboradorId}_mentoria`;
    if (effectivePdiId && colaboradorId) {
      loadSessoes(effectivePdiId);
    }
    loadMentores();
    loadColaboradores();
    if (competenciaId) {
      loadMentoresRecomendados();
    }
  }, [pdiId, colaboradorId, competenciaId]);

  const loadColaboradores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setColaboradores(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
    }
  };

  const loadMentores = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (competenciaId) {
        params.competencia = competenciaId;
      }
      
      const response = await axios.get(
        `${API}/api/pdi-completo/mentores-disponiveis`, 
        { 
          params,
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      setMentores(response.data.mentores || []);
    } catch (error) {
      console.error('Erro ao carregar mentores:', error);
    }
  };

  const loadMentoresRecomendados = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/pdi-completo/match-mentor`, 
        null, 
        {
          params: {
            mentorado_id: colaboradorId,
            competencia_desejada: competenciaId
          },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      setMentoresRecomendados(response.data.matches || []);
    } catch (error) {
      console.error('Erro ao fazer match:', error);
    }
  };

  const loadSessoes = async (effectivePdiId) => {
    try {
      const token = localStorage.getItem('token');
      const pdiIdToUse = effectivePdiId || pdiId || `pdi_${colaboradorId}_mentoria`;
      const response = await axios.get(
        `${API}/api/pdi-completo/sessoes-mentoria/${pdiIdToUse}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      setSessoes(response.data.sessoes || []);
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
    }
  };

  // ============ FUNÇÕES DE GESTÃO DE MENTORES ============
  
  const abrirModalMentor = (mentor = null) => {
    if (mentor) {
      setMentorEditando(mentor);
      setNovoMentor(mentor);
    } else {
      setMentorEditando(null);
      setNovoMentor({
        colaborador_id: '',
        nome: '',
        cargo: '',
        area: '',
        competencias_mentoria: [],
        anos_experiencia: 0,
        disponivel: true,
        capacidade_mentorados: 3,
        horas_disponiveis_semana: 2,
        bio: ''
      });
    }
    setShowMentorModal(true);
  };

  const salvarMentor = async () => {
    try {
      if (!novoMentor.colaborador_id || !novoMentor.nome) {
        toast.error('Preencha colaborador e nome');
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (mentorEditando) {
        // Atualizar mentor existente
        await axios.put(
          `${API}/api/pdi-completo/perfis-mentores/${mentorEditando.id}`,
          novoMentor,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        toast.success('Mentor atualizado com sucesso!');
      } else {
        // Criar novo mentor
        await axios.post(
          `${API}/api/pdi-completo/perfis-mentores`,
          novoMentor,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        toast.success('Mentor cadastrado com sucesso!');
      }
      
      setShowMentorModal(false);
      loadMentores();
      
    } catch (error) {
      console.error('Erro ao salvar mentor:', error);
      toast.error(`Erro ao salvar mentor: ${error.response?.data?.detail || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  };

  const desativarMentor = async (mentorId) => {
    if (!window.confirm('Deseja realmente desativar este mentor?')) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${API}/api/pdi-completo/perfis-mentores/${mentorId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      toast.success('Mentor desativado com sucesso!');
      loadMentores();
      
    } catch (error) {
      console.error('Erro ao desativar mentor:', error);
      toast.error('Erro ao desativar mentor');
    } finally {
      setLoading(false);
    }
  };

  const selecionarColaborador = (colaboradorId) => {
    const colaborador = colaboradores.find(c => c.id === colaboradorId);
    if (colaborador) {
      setNovoMentor(prev => ({
        ...prev,
        colaborador_id: colaboradorId,
        nome: colaborador.nome || colaborador.name || '',
        cargo: colaborador.cargo || colaborador.position || '',
        area: colaborador.departamento || colaborador.department || ''
      }));
    }
  };

  const adicionarCompetencia = (competencia) => {
    if (competencia && !novoMentor.competencias_mentoria.includes(competencia)) {
      setNovoMentor(prev => ({
        ...prev,
        competencias_mentoria: [...prev.competencias_mentoria, competencia]
      }));
    }
  };

  const removerCompetencia = (competencia) => {
    setNovoMentor(prev => ({
      ...prev,
      competencias_mentoria: prev.competencias_mentoria.filter(c => c !== competencia)
    }));
  };

  const abrirModalAgendamento = (mentor) => {
    setMentorSelecionado(mentor);
    setNovaSessao(prev => ({
      ...prev,
      mentor_id: mentor.id
    }));
    setShowAgendarModal(true);
  };

  const abrirDetalhes = (sessao) => {
    setSessaoSelecionada(sessao);
    setShowDetalhesModal(true);
  };

  const cancelarSessao = async (sessaoId) => {
    if (!window.confirm('Deseja realmente cancelar esta sessão de mentoria?')) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API}/api/pdi-completo/sessoes-mentoria/${sessaoId}/cancelar`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      toast.success('Sessão cancelada com sucesso!');
      const effectivePdiId = pdiId || `pdi_${colaboradorId}_mentoria`;
      loadSessoes(effectivePdiId);
      
    } catch (error) {
      console.error('Erro ao cancelar sessão:', error);
      toast.error('Erro ao cancelar sessão');
    } finally {
      setLoading(false);
    }
  };

  const reagendarSessao = (sessao) => {
    // Preencher o formulário com dados da sessão atual
    setNovaSessao({
      ...novaSessao,
      mentor_id: sessao.mentor_id,
      data_agendada: sessao.data_agendada,
      duracao_minutos: sessao.duracao_minutos,
      objetivo: sessao.objetivo,
      topicos: sessao.topicos || []
    });
    
    // Salvar ID da sessão antiga para atualizar
    setSessaoSelecionada(sessao);
    
    // Buscar mentor para preencher o modal
    const mentor = mentores.find(m => m.id === sessao.mentor_id);
    if (mentor) {
      setMentorSelecionado(mentor);
    }
    
    setShowAgendarModal(true);
  };

  const agendarSessao = async () => {
    try {
      if (!novaSessao.data_agendada || !novaSessao.objetivo) {
        toast.error('Preencha data e objetivo');
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Sessão expirada. Faça login novamente.');
        window.location.href = '/login';
        return;
      }
      
      await axios.post(
        `${API}/api/pdi-completo/sessoes-mentoria`, 
        novaSessao,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      toast.success('Sessão agendada com sucesso!');
      setShowAgendarModal(false);
      
      // Recarregar sessões com o pdi_id correto
      const effectivePdiId = pdiId || `pdi_${colaboradorId}_mentoria`;
      loadSessoes(effectivePdiId);
      
      // Reset form
      setNovaSessao({
        pdi_id: pdiId || `pdi_${colaboradorId}_mentoria`,
        mentorado_id: colaboradorId,
        mentor_id: '',
        data_agendada: '',
        duracao_minutos: 60,
        objetivo: '',
        topicos: []
      });
      
    } catch (error) {
      console.error('Erro ao agendar sessão:', error);
      
      // Verificar se é erro de autenticação
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Sessão expirada. Por favor, faça login novamente.');
        // Limpar token
        localStorage.removeItem('token');
        // Redirecionar para login após 2 segundos
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        const errorMsg = error.response?.data?.detail || 'Erro desconhecido';
        toast.error(`Erro ao agendar sessão: ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'agendada': 'bg-blue-100 text-blue-800',
      'realizada': 'bg-green-100 text-green-800',
      'cancelada': 'bg-red-100 text-red-800',
      'reagendada': 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || colors['agendada'];
  };

  return (
    <div className="space-y-6">
      {/* Tabs de Navegação */}
      <Card>
        <CardContent className="p-0">
          <div className="flex border-b">
            <button
              className={`flex-1 px-6 py-4 font-semibold flex items-center justify-center space-x-2 ${
                abaAtiva === 'sessoes' 
                  ? 'border-b-2 border-purple-600 text-purple-600 bg-purple-50' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setAbaAtiva('sessoes')}
            >
              <Calendar className="h-5 w-5" />
              <span>Sessões de Mentoria</span>
            </button>
            <button
              className={`flex-1 px-6 py-4 font-semibold flex items-center justify-center space-x-2 ${
                abaAtiva === 'mentores' 
                  ? 'border-b-2 border-purple-600 text-purple-600 bg-purple-50' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setAbaAtiva('mentores')}
            >
              <Users className="h-5 w-5" />
              <span>Gestão de Mentores</span>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Conteúdo da Aba Sessões */}
      {abaAtiva === 'sessoes' && (
        <>
          {/* Mentores Recomendados */}
          {mentoresRecomendados.length > 0 && (
            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  <span>Mentores Recomendados</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {mentoresRecomendados.map((match) => (
                <Card key={match.mentor_id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="text-center mb-3">
                      <div className="w-16 h-16 bg-purple-600 rounded-full mx-auto mb-2 flex items-center justify-center text-white text-2xl font-bold">
                        {match.nome.charAt(0)}
                      </div>
                      <h4 className="font-semibold">{match.nome}</h4>
                      <p className="text-sm text-gray-600">{match.cargo}</p>
                    </div>
                    
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Match:</span>
                        <Badge className="bg-purple-600 text-white">
                          {match.score_match}%
                        </Badge>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Star className="h-4 w-4 text-yellow-500 mr-1 fill-current" />
                        <span>{match.avaliacao.toFixed(1)}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Award className="h-4 w-4 mr-1" />
                        <span>{match.experiencia} anos</span>
                      </div>
                    </div>
                    
                    <Button 
                      size="sm" 
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      onClick={() => abrirModalAgendamento(match)}
                    >
                      Agendar Mentoria
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Todos os Mentores */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5 text-blue-600" />
            <span>Mentores Disponíveis ({mentores.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mentores.length === 0 ? (
            <div className="text-center py-8">
              <UserCheck className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nenhum mentor disponível no momento</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {mentores.map((mentor) => (
                <Card key={mentor.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-blue-600 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold">
                        {mentor.nome.charAt(0)}
                      </div>
                      <h5 className="font-semibold text-sm mb-1">{mentor.nome}</h5>
                      <p className="text-xs text-gray-600 mb-2">{mentor.cargo}</p>
                      {mentor.avaliacao_media > 0 && (
                        <div className="flex items-center justify-center text-xs mb-2">
                          <Star className="h-3 w-3 text-yellow-500 mr-1 fill-current" />
                          <span>{mentor.avaliacao_media.toFixed(1)}</span>
                        </div>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="w-full text-xs"
                        onClick={() => abrirModalAgendamento(mentor)}
                      >
                        Agendar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sessões Agendadas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-green-600" />
            <span>Minhas Sessões ({sessoes.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sessoes.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nenhuma sessão agendada</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sessoes.map((sessao) => (
                <Card key={sessao.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h5 className="font-semibold">
                            {sessao.mentor_nome || 'Mentor'}
                          </h5>
                          <Badge className={getStatusColor(sessao.status)}>
                            {sessao.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{sessao.objetivo}</p>
                        <div className="flex items-center space-x-4 text-xs text-gray-600">
                          <div className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {new Date(sessao.data_agendada).toLocaleDateString('pt-BR')}
                          </div>
                          <div className="flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {new Date(sessao.data_agendada).toLocaleTimeString('pt-BR', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </div>
                          <div className="flex items-center text-gray-500">
                            <span>•</span>
                            <span className="ml-1">{sessao.duracao_minutos} min</span>
                          </div>
                          {sessao.link_virtual && (
                            <div className="flex items-center">
                              <Video className="h-3 w-3 mr-1" />
                              Virtual
                            </div>
                          )}
                        </div>
                      </div>
                      {sessao.status === 'agendada' && (
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => abrirDetalhes(sessao)}
                          >
                            Detalhes
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => reagendarSessao(sessao)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Calendar className="h-3 w-3 mr-1" />
                            Reagendar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => cancelarSessao(sessao.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Cancelar
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Agendamento */}
      <Dialog open={showAgendarModal} onOpenChange={setShowAgendarModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Agendar Sessão de Mentoria</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {mentorSelecionado && (
              <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                  {mentorSelecionado.nome?.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold">{mentorSelecionado.nome}</div>
                  <div className="text-sm text-gray-600">{mentorSelecionado.cargo}</div>
                </div>
              </div>
            )}

            <div>
              <Label>Data e Hora</Label>
              <Input
                type="datetime-local"
                value={novaSessao.data_agendada}
                onChange={(e) => setNovaSessao({...novaSessao, data_agendada: e.target.value})}
              />
            </div>

            <div>
              <Label>Duração (minutos)</Label>
              <Input
                type="number"
                value={novaSessao.duracao_minutos}
                onChange={(e) => setNovaSessao({...novaSessao, duracao_minutos: parseInt(e.target.value)})}
              />
            </div>

            <div>
              <Label>Objetivo da Sessão</Label>
              <Textarea
                value={novaSessao.objetivo}
                onChange={(e) => setNovaSessao({...novaSessao, objetivo: e.target.value})}
                placeholder="Descreva o que você gostaria de trabalhar nesta sessão..."
                rows={3}
              />
            </div>

            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowAgendarModal(false)}
              >
                Cancelar
              </Button>
              <Button 
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={agendarSessao}
                disabled={loading}
              >
                {loading ? 'Agendando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Detalhes da Sessão */}
      <Dialog open={showDetalhesModal} onOpenChange={setShowDetalhesModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes da Sessão de Mentoria</DialogTitle>
          </DialogHeader>
          {sessaoSelecionada && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-sm font-semibold text-gray-600">Mentor</Label>
                <p className="text-lg font-semibold mt-1">{sessaoSelecionada.mentor_nome || 'Mentor'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Data</Label>
                  <div className="flex items-center mt-1">
                    <Calendar className="h-4 w-4 mr-2 text-purple-600" />
                    <span>{new Date(sessaoSelecionada.data_agendada).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Horário</Label>
                  <div className="flex items-center mt-1">
                    <Clock className="h-4 w-4 mr-2 text-purple-600" />
                    <span>
                      {new Date(sessaoSelecionada.data_agendada).toLocaleTimeString('pt-BR', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-600">Duração</Label>
                <p className="mt-1">{sessaoSelecionada.duracao_minutos} minutos</p>
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-600">Objetivo</Label>
                <p className="mt-1 text-gray-700">{sessaoSelecionada.objetivo}</p>
              </div>

              {sessaoSelecionada.topicos && sessaoSelecionada.topicos.length > 0 && (
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Tópicos</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {sessaoSelecionada.topicos.map((topico, idx) => (
                      <Badge key={idx} variant="secondary">{topico}</Badge>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-sm font-semibold text-gray-600">Status</Label>
                <div className="mt-1">
                  <Badge className={getStatusColor(sessaoSelecionada.status)}>
                    {sessaoSelecionada.status}
                  </Badge>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowDetalhesModal(false)}
                >
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>
      )}

      {/* Conteúdo da Aba Gestão de Mentores */}
      {abaAtiva === 'mentores' && (
        <>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-5 w-5" />
                  <span>Mentores Cadastrados</span>
                  <Badge className="ml-2">{mentores.length}</Badge>
                </CardTitle>
                <Button
                  onClick={() => abrirModalMentor()}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Cadastrar Mentor
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {mentores.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">Nenhum mentor cadastrado ainda</p>
                  <Button onClick={() => abrirModalMentor()} variant="outline">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Cadastrar Primeiro Mentor
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {mentores.map((mentor) => (
                    <Card key={mentor.id} className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                              {mentor.nome?.charAt(0)}
                            </div>
                            <div>
                              <div className="font-semibold">{mentor.nome}</div>
                              <div className="text-sm text-gray-600">{mentor.cargo}</div>
                            </div>
                          </div>
                          <div className="flex space-x-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => abrirModalMentor(mentor)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => desativarMentor(mentor.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center text-gray-600">
                            <Award className="h-4 w-4 mr-2" />
                            <span>{mentor.area}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Star className="h-4 w-4 mr-2 text-yellow-500" />
                            <span>{mentor.anos_experiencia} anos de experiência</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <Clock className="h-4 w-4 mr-2" />
                            <span>{mentor.horas_disponiveis_semana}h/semana disponíveis</span>
                          </div>
                        </div>

                        {mentor.competencias_mentoria && mentor.competencias_mentoria.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1">
                            {mentor.competencias_mentoria.slice(0, 3).map((comp, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {comp}
                              </Badge>
                            ))}
                            {mentor.competencias_mentoria.length > 3 && (
                              <Badge variant="secondary" className="text-xs">
                                +{mentor.competencias_mentoria.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}

                        <div className="mt-3 flex items-center justify-between">
                          <Badge className={mentor.disponivel ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                            {mentor.disponivel ? 'Disponível' : 'Indisponível'}
                          </Badge>
                          <span className="text-xs text-gray-600">
                            {mentor.mentorados_atuais || 0}/{mentor.capacidade_mentorados} mentorados
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Modal de Cadastro/Edição de Mentor */}
          <Dialog open={showMentorModal} onOpenChange={setShowMentorModal}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {mentorEditando ? 'Editar Mentor' : 'Cadastrar Novo Mentor'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Colaborador *</Label>
                  <Select
                    value={novoMentor.colaborador_id}
                    onValueChange={selecionarColaborador}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um colaborador" />
                    </SelectTrigger>
                    <SelectContent>
                      {colaboradores.map((colab) => (
                        <SelectItem key={colab.id} value={colab.id}>
                          {colab.nome || colab.name} - {colab.cargo || colab.position}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nome *</Label>
                    <Input
                      value={novoMentor.nome}
                      onChange={(e) => setNovoMentor({...novoMentor, nome: e.target.value})}
                      placeholder="Nome completo"
                    />
                  </div>
                  <div>
                    <Label>Cargo</Label>
                    <Input
                      value={novoMentor.cargo}
                      onChange={(e) => setNovoMentor({...novoMentor, cargo: e.target.value})}
                      placeholder="Ex: Gerente de TI"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Área/Departamento</Label>
                    <Input
                      value={novoMentor.area}
                      onChange={(e) => setNovoMentor({...novoMentor, area: e.target.value})}
                      placeholder="Ex: Tecnologia"
                    />
                  </div>
                  <div>
                    <Label>Anos de Experiência</Label>
                    <Input
                      type="number"
                      value={novoMentor.anos_experiencia}
                      onChange={(e) => setNovoMentor({...novoMentor, anos_experiencia: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>

                <div>
                  <Label>Competências de Mentoria</Label>
                  <div className="flex space-x-2 mb-2">
                    <Input
                      id="nova-competencia"
                      placeholder="Digite uma competência e pressione Enter"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          adicionarCompetencia(e.target.value);
                          e.target.value = '';
                        }
                      }}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {novoMentor.competencias_mentoria.map((comp, idx) => (
                      <Badge key={idx} className="flex items-center space-x-1">
                        <span>{comp}</span>
                        <button onClick={() => removerCompetencia(comp)}>
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Capacidade de Mentorados</Label>
                    <Input
                      type="number"
                      value={novoMentor.capacidade_mentorados}
                      onChange={(e) => setNovoMentor({...novoMentor, capacidade_mentorados: parseInt(e.target.value) || 3})}
                    />
                  </div>
                  <div>
                    <Label>Horas Disponíveis/Semana</Label>
                    <Input
                      type="number"
                      value={novoMentor.horas_disponiveis_semana}
                      onChange={(e) => setNovoMentor({...novoMentor, horas_disponiveis_semana: parseInt(e.target.value) || 2})}
                    />
                  </div>
                </div>

                <div>
                  <Label>Bio/Apresentação</Label>
                  <Textarea
                    value={novoMentor.bio}
                    onChange={(e) => setNovoMentor({...novoMentor, bio: e.target.value})}
                    placeholder="Conte um pouco sobre sua experiência e áreas de expertise..."
                    rows={3}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="disponivel"
                    checked={novoMentor.disponivel}
                    onChange={(e) => setNovoMentor({...novoMentor, disponivel: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="disponivel">Disponível para novos mentorados</Label>
                </div>

                <div className="flex space-x-2 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowMentorModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    onClick={salvarMentor}
                    disabled={loading}
                  >
                    {loading ? 'Salvando...' : mentorEditando ? 'Atualizar' : 'Cadastrar'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default SistemaMentoria;
